import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getStagedVoyageCandidates,
  searchVoyagesForStaged,
} from '../services/StagingRepository';

/**
 * Manages candidate loading and search for the staging review modal.
 */
const useStagingCandidates = ({ open, stagedVoyage }) => {
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [error, setError] = useState(null);

  const loadCandidates = useCallback(async () => {
    if (!stagedVoyage) return;
    setLoadingCandidates(true);
    setError(null);
    try {
      const data = await getStagedVoyageCandidates(stagedVoyage.id);
      setCandidates(data || []);
    } catch {
      setCandidates([]);
      setError('Failed to load match candidates');
    } finally {
      setLoadingCandidates(false);
    }
  }, [stagedVoyage]);

  useEffect(() => {
    if (open && stagedVoyage) {
      loadCandidates();
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [open, stagedVoyage, loadCandidates]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoadingSearch(true);
    setError(null);
    try {
      const results = await searchVoyagesForStaged(
        stagedVoyage.id,
        searchQuery
      );
      setSearchResults(results || []);
    } catch {
      setSearchResults([]);
      setError('Search failed');
    } finally {
      setLoadingSearch(false);
    }
  };

  const allCandidates = useMemo(
    () => [
      ...candidates,
      ...searchResults.filter(
        (sr) => !candidates.some((c) => c.voyageId === sr.voyageId)
      ),
    ],
    [candidates, searchResults]
  );

  return {
    candidates: allCandidates,
    loadingCandidates,
    searchQuery,
    setSearchQuery,
    loadingSearch,
    handleSearch,
    candidateError: error,
  };
};

export default useStagingCandidates;
